import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Calculator, CurrencyDollar, Warning, ChartLineUp,
  ArrowRight, Info, Buildings, TrendDown, Sparkle
} from "@phosphor-icons/react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const ROICalculatorPage = () => {
  const [formData, setFormData] = useState({
    avgDailyCensus: 50,
    avgReimbursementPerDay: 200,
    surveyDeficiencies: 2,
    referralConversionRate: 60,
    monthlyReferrals: 40,
    staffTurnoverRate: 25,
    avgSalary: 55000
  });
  const [results, setResults] = useState(null);
  const [showEmail, setShowEmail] = useState(false);
  const [email, setEmail] = useState("");
  const [emailSubmitted, setEmailSubmitted] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: parseFloat(value) || 0 });
  };

  const calculateROI = () => {
    const {
      avgDailyCensus,
      avgReimbursementPerDay,
      surveyDeficiencies,
      referralConversionRate,
      monthlyReferrals,
      staffTurnoverRate,
      avgSalary
    } = formData;

    // Compliance Cost Calculations
    const pocCostPerDeficiency = 15000;
    const surveyRemediationCost = surveyDeficiencies * pocCostPerDeficiency;
    const potentialCMSFines = surveyDeficiencies > 3 ? 50000 : surveyDeficiencies > 1 ? 25000 : 0;
    const complianceTotalCost = surveyRemediationCost + potentialCMSFines;

    // Lost Revenue from Poor Conversion
    const potentialAdmissions = monthlyReferrals * 12;
    const currentAdmissions = potentialAdmissions * (referralConversionRate / 100);
    const industryBenchmark = 0.75;
    const potentialWithBenchmark = potentialAdmissions * industryBenchmark;
    const lostAdmissions = potentialWithBenchmark - currentAdmissions;
    const avgLOS = 60;
    const lostRevenueFromConversion = lostAdmissions * avgLOS * avgReimbursementPerDay;

    // Turnover Costs
    const totalStaff = Math.ceil(avgDailyCensus / 8);
    const annualTurnover = totalStaff * (staffTurnoverRate / 100);
    const costPerTurnover = avgSalary * 0.5;
    const turnoverCost = annualTurnover * costPerTurnover;

    // Total Annual Impact
    const totalAnnualImpact = complianceTotalCost + lostRevenueFromConversion + turnoverCost;

    // Potential Improvement (conservative 30% improvement estimate)
    const potentialSavings = totalAnnualImpact * 0.3;

    setResults({
      complianceCost: complianceTotalCost,
      surveyRemediation: surveyRemediationCost,
      potentialFines: potentialCMSFines,
      lostRevenue: lostRevenueFromConversion,
      lostAdmissions: Math.round(lostAdmissions),
      turnoverCost: Math.round(turnoverCost),
      annualTurnover: Math.round(annualTurnover),
      totalImpact: Math.round(totalAnnualImpact),
      potentialSavings: Math.round(potentialSavings)
    });
    setShowEmail(true);
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/api/roi-calculator`, {
        email,
        inputs: formData,
        results
      });
      setEmailSubmitted(true);
    } catch (err) {
      console.error("Failed to save:", err);
      setEmailSubmitted(true);
    }
  };

  const formatCurrency = (num) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(num);
  };

  return (
    <div data-testid="roi-calculator-page" className="min-h-screen bg-gradient-to-b from-muted/30 to-white">
      {/* Hero */}
      <section className="py-16 bg-hero-gradient relative overflow-hidden">
        <div className="decorative-blob w-96 h-96 bg-primary/20 -top-48 -right-48" />
        <div className="max-w-4xl mx-auto px-6 text-center relative">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <span className="badge badge-primary mb-4">
              <Calculator weight="fill" className="w-4 h-4 mr-2" />
              Free Tool
            </span>
            <h1 className="font-display text-4xl md:text-5xl text-foreground mb-4">
              ROI Calculator
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Discover the true cost of operational inefficiencies and compliance gaps. 
              See how much your organization could save with strategic improvements.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Input Form */}
            <div className="bg-white rounded-2xl shadow-lg border border-border p-8">
              <h2 className="font-display text-xl text-foreground mb-6 flex items-center gap-2">
                <Buildings weight="duotone" className="w-6 h-6 text-primary" />
                Your Organization Profile
              </h2>

              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Average Daily Census
                  </label>
                  <input
                    type="number"
                    value={formData.avgDailyCensus}
                    onChange={(e) => handleChange("avgDailyCensus", e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-border focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Average Reimbursement Per Patient Day ($)
                  </label>
                  <input
                    type="number"
                    value={formData.avgReimbursementPerDay}
                    onChange={(e) => handleChange("avgReimbursementPerDay", e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-border focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Survey Deficiencies (Last Survey)
                  </label>
                  <input
                    type="number"
                    value={formData.surveyDeficiencies}
                    onChange={(e) => handleChange("surveyDeficiencies", e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-border focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Referral-to-Admission Conversion Rate (%)
                  </label>
                  <input
                    type="number"
                    value={formData.referralConversionRate}
                    onChange={(e) => handleChange("referralConversionRate", e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-border focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Industry benchmark: 75%</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Monthly Referrals Received
                  </label>
                  <input
                    type="number"
                    value={formData.monthlyReferrals}
                    onChange={(e) => handleChange("monthlyReferrals", e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-border focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Annual Staff Turnover Rate (%)
                  </label>
                  <input
                    type="number"
                    value={formData.staffTurnoverRate}
                    onChange={(e) => handleChange("staffTurnoverRate", e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-border focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Industry average: 20-30%</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Average Staff Salary ($)
                  </label>
                  <input
                    type="number"
                    value={formData.avgSalary}
                    onChange={(e) => handleChange("avgSalary", e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-border focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  />
                </div>

                <button
                  onClick={calculateROI}
                  className="w-full btn-primary"
                  data-testid="calculate-btn"
                >
                  Calculate My ROI
                </button>
              </div>
            </div>

            {/* Results */}
            <div className="space-y-6">
              {results ? (
                <>
                  {/* Total Impact Card */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-gradient-to-br from-red-500 to-red-600 rounded-2xl p-8 text-white"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <TrendDown weight="bold" className="w-6 h-6" />
                      <span className="text-white/80 font-medium">Estimated Annual Impact</span>
                    </div>
                    <div className="text-4xl md:text-5xl font-display mb-2">
                      {formatCurrency(results.totalImpact)}
                    </div>
                    <p className="text-white/70 text-sm">
                      Total cost of compliance gaps, lost revenue, and turnover
                    </p>
                  </motion.div>

                  {/* Breakdown Cards */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="bg-white rounded-2xl shadow-lg border border-border p-6"
                  >
                    <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                      <Warning weight="fill" className="w-5 h-5 text-orange-500" />
                      Compliance Costs
                    </h3>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Survey Remediation</span>
                        <span className="font-semibold text-foreground">{formatCurrency(results.surveyRemediation)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Potential CMS Fines</span>
                        <span className="font-semibold text-foreground">{formatCurrency(results.potentialFines)}</span>
                      </div>
                      <div className="border-t pt-3 flex justify-between">
                        <span className="font-medium text-foreground">Subtotal</span>
                        <span className="font-bold text-red-600">{formatCurrency(results.complianceCost)}</span>
                      </div>
                    </div>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="bg-white rounded-2xl shadow-lg border border-border p-6"
                  >
                    <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                      <CurrencyDollar weight="fill" className="w-5 h-5 text-red-500" />
                      Lost Revenue (Conversion Gap)
                    </h3>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Lost Admissions/Year</span>
                        <span className="font-semibold text-foreground">{results.lostAdmissions} patients</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Revenue Impact</span>
                        <span className="font-bold text-red-600">{formatCurrency(results.lostRevenue)}</span>
                      </div>
                    </div>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="bg-white rounded-2xl shadow-lg border border-border p-6"
                  >
                    <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                      <Warning weight="fill" className="w-5 h-5 text-yellow-500" />
                      Staff Turnover Costs
                    </h3>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Annual Turnover</span>
                        <span className="font-semibold text-foreground">{results.annualTurnover} employees</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Replacement Cost</span>
                        <span className="font-bold text-red-600">{formatCurrency(results.turnoverCost)}</span>
                      </div>
                    </div>
                  </motion.div>

                  {/* Potential Savings */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl p-8 text-white"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <ChartLineUp weight="bold" className="w-6 h-6" />
                      <span className="text-white/80 font-medium">Potential Annual Savings</span>
                    </div>
                    <div className="text-4xl md:text-5xl font-display mb-2">
                      {formatCurrency(results.potentialSavings)}
                    </div>
                    <p className="text-white/70 text-sm">
                      With 30% improvement in operations, compliance, and retention
                    </p>
                  </motion.div>

                  {/* Email Capture */}
                  {showEmail && !emailSubmitted && (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.5 }}
                      className="bg-white rounded-2xl shadow-lg border border-border p-6"
                    >
                      <h3 className="font-semibold text-foreground mb-2">Get Your Full Report</h3>
                      <p className="text-sm text-muted-foreground mb-4">
                        Receive a detailed breakdown with specific improvement strategies.
                      </p>
                      <form onSubmit={handleEmailSubmit} className="flex gap-3">
                        <input
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="your@email.com"
                          className="flex-1 px-4 py-3 rounded-xl border border-border focus:ring-2 focus:ring-primary/20"
                          required
                        />
                        <button type="submit" className="btn-primary">Send</button>
                      </form>
                    </motion.div>
                  )}

                  {/* CTA */}
                  <div className="bg-muted/50 rounded-2xl p-6 text-center">
                    <Sparkle weight="fill" className="w-8 h-8 text-accent mx-auto mb-3" />
                    <h3 className="font-display text-lg text-foreground mb-2">
                      Ready to Capture These Savings?
                    </h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Let's discuss a customized improvement plan for your organization.
                    </p>
                    <Link to="/schedule-demo" className="btn-primary inline-flex items-center gap-2">
                      Schedule Strategy Session
                      <ArrowRight weight="bold" className="w-5 h-5" />
                    </Link>
                  </div>
                </>
              ) : (
                <div className="bg-muted/30 rounded-2xl p-12 text-center">
                  <Calculator weight="duotone" className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                  <h3 className="font-display text-xl text-foreground mb-2">Enter Your Data</h3>
                  <p className="text-muted-foreground">
                    Fill in your organization's information to see your potential ROI from operational improvements.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ROICalculatorPage;
